import { BarChart3 } from 'lucide-react';
import { formatPercent, formatCurrency } from '../../utils/formatters';
import { SectionTitle } from '../Luxury';
import type { ThreeCapRates } from '../../types/calculations';

interface ThreeCapRatesCardProps {
  data: ThreeCapRates;
}

const BADGE_STYLES: Record<string, string> = {
  gray: 'bg-stone-700/50 text-stone-300 border border-stone-600/30',
  blue: 'bg-blue-900/30 text-blue-300 border border-blue-600/20',
  green: 'bg-emerald/10 text-emerald border border-emerald/20',
};

function getCapRateColor(rate: number): string {
  if (rate >= 0.10) return 'text-emerald';
  if (rate >= 0.07) return 'text-blue-400';
  if (rate >= 0.05) return 'text-amber-400';
  return 'text-crimson';
}

interface CapMetric {
  key: string;
  label: string;
  sublabel: string;
  tooltip: string;
  displayValue: string;
  colorClass: string;
  formula: string | undefined;
  badge: string;
  note: string | null;
}

export function ThreeCapRatesCard({ data }: ThreeCapRatesCardProps) {
  const capMetrics: CapMetric[] = [
    {
      key: 'entry_cap',
      label: 'Entry Cap',
      sublabel: 'Uplifted NOI ÷ Offer Price',
      tooltip: 'Post-renovation NOI at market rents, divided by your offer price. This is the return potential at your price.',
      displayValue: formatPercent(data.entry_cap * 100, 1),
      colorClass: getCapRateColor(data.entry_cap),
      formula: data.formulas.entry_cap,
      badge: 'gray',
      note: null,
    },
    {
      key: 'stabilized_value',
      label: 'Stabilized Value',
      sublabel: 'Uplifted NOI ÷ Market Cap',
      tooltip: 'Implied market value after renovation — what a buyer would pay for the stabilized property at the market cap rate.',
      displayValue: formatCurrency(data.stabilized_value),
      colorClass: 'text-blue-400',
      formula: data.formulas.stabilized_value,
      badge: 'blue',
      note: null,
    },
    {
      key: 'yield_on_cost',
      label: 'Yield on Cost',
      sublabel: 'Uplifted NOI ÷ Total Investment',
      tooltip: 'Post-renovation NOI at market rents, divided by total investment (purchase + CapEx + carry). Measures all-in return.',
      displayValue: formatPercent(data.yield_on_cost * 100, 1),
      colorClass: getCapRateColor(data.yield_on_cost),
      formula: data.formulas.yield_on_cost,
      badge: 'green',
      note: null,
    },
  ];

  // Value-Add Spread: YOC vs Entry Cap in basis points
  const spreadBps = Math.round((data.yield_on_cost - data.entry_cap) * 10000);

  const spreadColor =
    spreadBps >= 200 ? 'text-gold' :
    spreadBps >= 100 ? 'text-amber-400' :
    'text-stone-400';

  const spreadBarColor =
    spreadBps >= 200 ? 'bg-gold' :
    spreadBps >= 100 ? 'bg-amber-400' :
    'bg-stone-500';

  return (
    <div className="bg-obsidian rounded-xl p-6">
      <SectionTitle icon={<BarChart3 className="w-5 h-5" />}>
        Cap Rate Analysis
      </SectionTitle>

      <div className="grid grid-cols-3 gap-4 mt-4">
        {capMetrics.map((cap) => (
          <div
            key={cap.key}
            title={cap.tooltip}
            className="text-center p-4 bg-charcoal border border-slate-dark rounded-lg cursor-help"
          >
            <span className={`inline-block px-2.5 py-0.5 rounded-full text-sm uppercase tracking-wider font-semibold mb-2 ${BADGE_STYLES[cap.badge]}`}>
              {cap.label}
            </span>
            <p className={`text-3xl font-bold font-mono tabular-nums ${cap.colorClass}`}>
              {cap.displayValue}
            </p>
            <p className="text-sm text-stone-400 mt-2 font-mono">{cap.sublabel}</p>
            <p className="text-sm text-stone-400 mt-1 font-mono">{cap.formula}</p>
            {cap.note && (
              <p className="text-sm text-amber-400 mt-2 italic">{cap.note}</p>
            )}
          </div>
        ))}
      </div>

      {/* Value-Add Spread */}
      {spreadBps > 0 && (
        <div className="mt-6 p-4 bg-charcoal border border-slate-dark rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm uppercase tracking-[0.12em] text-stone-400 font-semibold">
              Value-Add Spread
            </span>
            <span className={`font-mono tabular-nums font-bold text-lg ${spreadColor}`}>
              +{spreadBps} bps
            </span>
          </div>

          {/* Bar visualization: Entry Cap to YOC */}
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-stone-400 mb-1">
              <span>Entry {formatPercent(data.entry_cap * 100, 1)}</span>
              <span className="flex-1" />
              <span>YOC {formatPercent(data.yield_on_cost * 100, 1)}</span>
            </div>
            <div className="relative h-3 bg-white/5 rounded-full overflow-hidden">
              {(() => {
                const maxRate = Math.max(data.yield_on_cost, data.entry_cap * 1.5);
                const basePct = (data.entry_cap / maxRate) * 100;
                const spreadPct = ((data.yield_on_cost - data.entry_cap) / maxRate) * 100;
                return (
                  <>
                    <div
                      className="absolute inset-y-0 left-0 bg-stone-600/60 rounded-l-full"
                      style={{ width: `${basePct}%` }}
                    />
                    <div
                      className={`absolute inset-y-0 ${spreadBarColor} rounded-r-full`}
                      style={{ left: `${basePct}%`, width: `${spreadPct}%` }}
                    />
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-3 mt-2 text-sm">
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-stone-600/60" />
                <span className="text-stone-400">Current Return</span>
              </span>
              <span className="flex items-center gap-1">
                <span className={`inline-block w-2 h-2 rounded-full ${spreadBarColor}`} />
                <span className={spreadColor}>Value-Add Uplift</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Interpretation guide */}
      <div className="mt-4 text-sm text-stone-400 bg-charcoal border border-slate-dark p-3 rounded-lg space-y-1.5">
        <p>
          <span className="text-ivory/70 font-medium">Entry Cap:</span>{' '}
          <span className="font-mono text-stone-400">Uplifted NOI ÷ Offer Price</span>
          {' '}&mdash; post-renovation return potential at your offer price
        </p>
        <p>
          <span className="text-ivory/70 font-medium">Stabilized Value:</span>{' '}
          <span className="font-mono text-stone-400">Uplifted NOI ÷ Market Cap Rate</span>
          {' '}&mdash; what this property is worth after renovation at market pricing
        </p>
        <p>
          <span className="text-ivory/70 font-medium">Yield on Cost:</span>{' '}
          <span className="font-mono text-stone-400">Uplifted NOI ÷ Total Investment</span>
          {' '}&mdash; post-renovation NOI divided by purchase + CapEx + carry costs
        </p>
      </div>
    </div>
  );
}
